import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { IPC_CHANNEL, STANDALONE } from '../../_offline/electron.constants';
import { IndividualReportQueryOpt, IndividualReportSchema } from '../../_offline/models/individual-report.schema';
import { DatabaseBaseService } from '../base/database-base.service';
import { LoginUserOfflineService } from '../offline/login-user-offline.service';
import { SubjectFilter } from '../storage.service';
import { ElectronService } from '../../electron.service';

@Injectable({
  providedIn: 'root'
})
export class IndividualReportDbService extends DatabaseBaseService {

  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  constructor(
    protected electronService: ElectronService,
    protected loginUserService: LoginUserOfflineService
  ) {
    super(electronService, loginUserService);
  }

  getAll(inspectionId?: string, statusCode?: string, subjectFilter?: SubjectFilter, isOwn?: boolean,
    sort?: { active: string; direction: string; }, isNewestOnly = false): Observable<IndividualReportSchema[]> {
    const reportOpt = new IndividualReportQueryOpt(this.tenantId, this.userId,
      inspectionId, statusCode, subjectFilter, isOwn, sort, isNewestOnly);
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_LIST, reportOpt);
  }

  getAllOnPremise(inspectionId: string): Observable<IndividualReportSchema[]> {
    const reportOpt = new IndividualReportQueryOpt(STANDALONE.TENANT_ID, STANDALONE.USER_ID, inspectionId);
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_LIST, reportOpt);
  }

  get(reportId: string, version: number = null): Observable<IndividualReportSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_ONE, reportId, this.tenantId, this.userId, version);
  }

  getAllVersions(reportId: string): Observable<IndividualReportSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_ALL_VERSION, reportId, this.tenantId, this.userId);
  }

  getByInspectionIds(inspectionIds: string[]): Observable<IndividualReportSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_BY_INSPECTION_IDS, inspectionIds, this.tenantId, this.userId);
  }

  getByIds(ids: string[], isAliveOnly = false): Observable<IndividualReportSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_BY_IDS, ids, this.tenantId, this.userId, isAliveOnly);
  }

  put(schema: IndividualReportSchema): Observable<IndividualReportSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.PUT_ONE, schema);
  }

  putALL(schemas: IndividualReportSchema[]): Observable<IndividualReportSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.PUT_ALL, schemas);
  }

  putThumbsPath(reportId: string, filePath: string): Observable<IndividualReportSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.PUT_THUMB_PATH, reportId, filePath, this.tenantId);
  }

  delete(reportId: string, isLogicalDelete = true): Observable<string[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.DELETE_ONE, reportId, this.tenantId, isLogicalDelete);
  }

  deleteALL(inspectionId: string, isLogicalDelete = true): Observable<string[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.DELETE_ALL, inspectionId, this.tenantId, isLogicalDelete);
  }

  getReportsCountDetail(inspectionId: string): Observable<IndividualReportSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.GET_COUNT, inspectionId, this.tenantId);
  }

  /**
   * テンプレート ID を使用しているレポートが存在するかどうかを返す。
   *
   * @param reportTemplateId レポートテンプレート ID。
   * @returns 使用中のレポートが存在する場合は true を返す。
   */
  isInspectionReportUsingTemplateIdExists(reportTemplateId: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.INDIVIDUAL_REPORT.IS_INDIVIDUAL_REPORT_USING_TEMPLATEID_EXISTS, reportTemplateId, this.tenantId)
      .pipe(
        map(x => x as boolean)
      );
  }

}
